const isFiniteNumber = (value) =>
  typeof value === "number" && Number.isFinite(value);

const toFiniteNumberOrNull = (value) => {
  if (isFiniteNumber(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim()) {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const NUMBER_FORMATTER = new Intl.NumberFormat("en-US");
const COMPACT_NUMBER_FORMATTER = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});
const USD_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 2,
});
const USD_COMPACT_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  maximumFractionDigits: 1,
});

export const formatNumber = (value, fallback = "0") => {
  const numericValue = toFiniteNumberOrNull(value);
  return numericValue === null ? fallback : NUMBER_FORMATTER.format(numericValue);
};

export const formatCompactNumber = (value, fallback = "0") => {
  const numericValue = toFiniteNumberOrNull(value);
  return numericValue === null
    ? fallback
    : COMPACT_NUMBER_FORMATTER.format(numericValue);
};

export const formatUsd = (value, { compact = false, fallback = "$0" } = {}) => {
  const numericValue = toFiniteNumberOrNull(value);
  if (numericValue === null) {
    return fallback;
  }

  return compact
    ? USD_COMPACT_FORMATTER.format(numericValue)
    : USD_FORMATTER.format(numericValue);
};

export const formatPercent = (value, fallback = "N/A") => {
  const numericValue = toFiniteNumberOrNull(value);
  if (numericValue === null) {
    return fallback;
  }

  return `${numericValue.toFixed(2)}%`;
};

export const formatPrice = (value, unit = "REDDICKS", fallback = "N/A") => {
  const numericValue = toFiniteNumberOrNull(value);
  if (numericValue === null) {
    return fallback;
  }

  const decimals =
    Math.abs(numericValue) >= 1 ? 4 : Math.abs(numericValue) >= 0.01 ? 6 : 8;

  const formattedValue = numericValue.toLocaleString("en-US", {
    maximumFractionDigits: decimals,
  });

  return unit ? `${formattedValue} ${unit}` : formattedValue;
};

export const formatDateTime = (value, fallback = "N/A") => {
  if (!value) {
    return fallback;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return fallback;
  }

  return date.toLocaleString("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

export const formatDateOnly = (value, fallback = "N/A") => {
  if (!value) {
    return fallback;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return fallback;
  }

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const formatTrendTick = (unixSeconds) => {
  const timestamp = toFiniteNumberOrNull(unixSeconds);
  if (timestamp === null || timestamp <= 0) {
    return "";
  }

  return new Date(timestamp * 1000).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
};

export const formatTrendTooltipLabel = (unixSeconds) => {
  const timestamp = toFiniteNumberOrNull(unixSeconds);
  if (timestamp === null || timestamp <= 0) {
    return "Unknown";
  }

  return new Date(timestamp * 1000).toLocaleString("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

export const formatUnixSecondsDateTime = (unixSeconds, fallback = "N/A") => {
  const timestamp = toFiniteNumberOrNull(unixSeconds);
  if (timestamp === null || timestamp <= 0) {
    return fallback;
  }

  return new Date(timestamp * 1000).toLocaleString("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

export const formatShortageLabel = (value) => {
  if (typeof value !== "string" || !value.trim()) {
    return "N/A";
  }

  const labels = {
    BaseShortage: "Base Token Shortage",
    QuoteShortage: "Quote Token Shortage",
    NoShortage: "Balanced",
  };

  return labels[value] || value;
};
